// Builds the Athlete-Mode Player Profile view-model from the REAL athlete +
// cacheInformation (the /business/athletes/about response) + the athlete's
// circle members. Every value here is sourced from live data; when a data
// point is missing we surface an honest empty state (null / "-" / hidden
// section) rather than a fabricated placeholder.
//
// Field names match the backend exactly:
//   performance keys are human labels ("Points", "Assists", "True Shooting %")
//   percentages are already expanded to 0-100 (47.5 means 47.5%)
//   cacheValue/cacheShare: { min, max, from, to, tier }
//   nilRates: { events, socialPost, reel, story, longVideo, share }
//   circle members: { name, relationship, email, permission }

import { hometownParts, splitHometown } from "../../../utils/hometown";
import { PLAY_GROUP_LABELS, PLAY_GROUP_ORDER } from "../../../utils/playGroups";
import { displayTeamName, matchKey } from "../../../utils/teamNames";
import { collegeLevelOf, isMale } from "../v2/edit/editHubModel";

const num = (v) => {
  const n = typeof v === "string" ? parseFloat(v) : v;
  return typeof n === "number" && !isNaN(n) ? n : null;
};

const inchesToFeet = (inches) => {
  const n = num(inches);
  if (!n) return null;
  return `${Math.floor(n / 12)}'${Math.round(n % 12)}"`;
};

const fmt1 = (v) => (v == null ? null : (Math.round(v * 10) / 10).toString());
const pctStr = (v) => (v == null ? null : `${Math.round(v)}%`);

// A graduation year as the coach roster reads one (1950 to 2100, a number
// or its text); null for a blank, 0 or anything else, which falls through
// to the next source.
const yearOf = (v) => {
  if (typeof v !== "number" && typeof v !== "string") return null;
  const n = typeof v === "string" ? Number(v.trim() || NaN) : v;
  return Number.isFinite(n) && n >= 1950 && n <= 2100 ? Math.trunc(n) : null;
};

// levelOfPlay: 1 HS, 2 College, 3 Pro.
const LEVEL_NAMES = { 1: "HS", 2: "College", 3: "Pro" };

// A slot's team by the one display rule (utils/teamNames, D15): the
// server's displayName, else its name with the age group its league key
// names ("Oakland Soldiers 16U"), never a season CACHE added.
const nameOf = (item) => {
  const name = String(item?.name || "").trim();
  if (!item || typeof item !== "object" || !(name || item.displayName)) return name;
  return String(displayTeamName({ ...item, teamName: item.teamName || name }) || name).trim();
};
const sameText = (x, y) => !!String(x || "").trim() && String(x || "").trim().toLowerCase() === String(y || "").trim().toLowerCase();

// The athlete's teams and schools as the profile shows them, from the
// backend identity (athlete_identity): each team sits where its own kind
// puts it, so a college a team link filed in the Club field is the College,
// never the Club, and current and past teams are apart. `level` is the level
// the profile reads (identity.effectiveLevel: a graduated senior on a college
// team is College). Past high school, the high school shown is the last one;
// a pro's college is their University. An identity from before the slots
// (no `current`) reads its older keys by that level.
export const identitySlots = (identity = {}, level = 0) => {
  const ident = identity || {};
  const lists = {
    previousColleges: Array.isArray(ident.previousColleges) ? ident.previousColleges : [],
    previousHighSchools: Array.isArray(ident.previousHighSchools) ? ident.previousHighSchools : [],
    previousClubs: Array.isArray(ident.previousClubTeams) ? ident.previousClubTeams : [],
  };
  if (ident.current) {
    const cur = ident.current;
    return {
      college: level === 3 ? "" : nameOf(cur.college),
      university: level === 3 ? String(ident.school || "") : "",
      highSchool: level >= 2 ? String(ident.lastHighSchool || "") : nameOf(cur.highSchool),
      club: nameOf(cur.club),
      proTeam: nameOf(cur.proTeam),
      ...lists,
    };
  }
  const school = String(ident.school || "");
  const club = String(ident.club || "");
  const highSchool = level >= 2 ? String(ident.lastHighSchool || "") : school;
  return {
    college: level === 2 ? school : "",
    university: level === 3 ? school : "",
    highSchool,
    // The same text never shows twice.
    club: level === 3 || sameText(club, school) || sameText(club, highSchool) ? "" : club,
    proTeam: level === 3 ? club : "",
    ...lists,
  };
};

// The college level the profile shows: { code, text }. `code` is a level on
// file (the athlete's pick, SportsRadar's or Cerebro's class, the roster's),
// what the header subtitle shows; `text` is the About value, where an
// estimate from the class year reads "Freshman (estimated)". An identity
// from before the college level reads the athlete's pick, else Cerebro's.
export const collegeLevelShown = (identity = {}, athlete = {}, bio = {}, level = 0) => {
  const derived = identity?.collegeLevel;
  if (derived && typeof derived === "object") {
    const code = collegeLevelOf(derived.code);
    if (!code) return { code: "", text: "" };
    return derived.estimated ? { code: "", text: `${derived.label || code} (estimated)` } : { code, text: code };
  }
  const code = level === 2 ? collegeLevelOf(athlete.collegeLevel) || collegeLevelOf(bio?.class_standing) : "";
  return { code, text: code };
};

// The label of stats that stand in for the athlete's team feed (the about
// payload's statsStandIn {team, season}: the record of a team they have
// left, kept until their team's feed has their stats): "Stats from Lincoln
// High, 2025-26". "" for their own team's stats.
export const statsStandInLabel = (standIn) => {
  if (!standIn || typeof standIn !== "object") return "";
  const team = String(standIn.team || "").trim();
  const season = String(standIn.season || "").trim();
  if (team && season) return `Stats from ${team}, ${season}`;
  if (team || season) return `Stats from ${team || season}`;
  return "Stats from a previous team";
};

// The records the CacheScore's game stats count (the about payload's
// statsSources, one per record: {id, via, team, competition, scoringGames,
// totalGames}) as the box score's "Stats from" line (D18): "Stats from
// Oakland Soldiers 16U, 22 games · Brentwood School, 0 games". Each team
// once, by the display rule (utils/teamNames), with the games it counts
// toward the score (all its games when the count toward the score is not
// sent); the most games first; past four, "· 2 more". A record without a
// team reads by its competition; how it was found and its id never show.
// "" without a source, and when the stand-in label (statsStandIn) already
// names the only one: the label names the team as stored ("Oakland
// Soldiers"), the line by the display rule ("Oakland Soldiers 16U"), so the
// two are compared by matchKey, which reads both as one team.
const MAX_SOURCES_SHOWN = 4;

const sourceNameOf = (s) => {
  if (s.team && typeof s.team === "object") {
    const team = s.team;
    return textOf(displayTeamName(team)) || textOf(team.displayName) || textOf(team.name) || textOf(team.teamName);
  }
  const own = textOf(s.teamDisplayName) || textOf(s.displayName);
  if (own) return own;
  const team = textOf(s.team);
  if (team) return textOf(displayTeamName({ teamName: team, name: team, leagueKey: s.leagueKey || s.league, ageGroup: s.ageGroup })) || team;
  return textOf(s.competition);
};

export const statsSourcesLine = (sources, { standIn = null } = {}) => {
  const byName = new Map();
  (Array.isArray(sources) ? sources : [])
    .filter((s) => s && typeof s === "object")
    .forEach((s, order) => {
      const name = sourceNameOf(s);
      if (!name) return;
      const games = num(s.scoringGames) ?? num(s.totalGames);
      const seen = byName.get(name.toLowerCase());
      if (!seen) byName.set(name.toLowerCase(), { name, games, order });
      else if (games != null) seen.games = (seen.games || 0) + games;
    });
  const entries = [...byName.values()].sort((a, b) => (b.games || 0) - (a.games || 0) || a.order - b.order);
  if (!entries.length) return "";
  const standInTeam = standIn && typeof standIn === "object" ? matchKey(textOf(standIn.team)) : "";
  if (entries.length === 1 && standInTeam && matchKey(entries[0].name) === standInTeam) return "";
  const shown = entries.slice(0, MAX_SOURCES_SHOWN).map((e) => (e.games == null || e.games < 0 ? e.name : `${e.name}, ${countOf(e.games, "game")}`));
  const more = entries.length - shown.length;
  return `Stats from ${shown.join(" · ")}${more > 0 ? ` · ${more} more` : ""}`;
};

// A day as the profile writes it ("Oct 12, 2026"): a date alone reads as
// that day anywhere; a time without a zone is UTC, as the server stores it;
// unix seconds are a time too. "" for anything else.
const dayOf = (value) => {
  if (value == null || value === "" || typeof value === "boolean") return "";
  const text = String(value).trim();
  const dateOnly = /^\d{4}-\d{2}-\d{2}$/.test(text);
  let date;
  if (typeof value === "number") date = new Date(value * 1000);
  else if (dateOnly) date = new Date(`${text}T12:00:00Z`);
  else date = new Date(/(Z|[+-]\d\d:?\d\d)$/i.test(text) ? text : `${text}Z`);
  if (Number.isNaN(date.getTime())) return "";
  return date.toLocaleDateString("en-US", { month: "short", day: "numeric", year: "numeric", ...(dateOnly ? { timeZone: "UTC" } : {}) });
};

// The box score's notice while the stats are not live (the about payload's
// statsStatus {state, asOf}: the last refresh failed or came back short, so
// the stored stats stay): "Stats as of Oct 12, 2026. Live updates are
// paused." Its reason never shows (it can name the data provider). "" while
// they are live.
export const statsStaleNotice = (status) => {
  if (!status || typeof status !== "object" || String(status.state || "").toLowerCase() !== "stale") return "";
  const day = dayOf(status.asOf);
  return day ? `Stats as of ${day}. Live updates are paused.` : "Live updates to these stats are paused.";
};

// PerformanceValue™ (J2, the about payload's performanceValue {min, max,
// value}): the engine's low and high scenarios around its point value, so
// the meter's floor and ceiling are those and its mark is the point value,
// never the midpoint. null without one, or for a $0 range (no performance
// to value).
export const performanceRange = (obj) => {
  if (!obj || typeof obj !== "object") return null;
  const low = num(obj.min) ?? num(obj.from);
  const high = num(obj.max) ?? num(obj.to);
  const point = num(obj.value);
  if (low == null && high == null && point == null) return null;
  const floor = low ?? point ?? 0;
  const ceiling = Math.max(floor, high ?? point ?? floor);
  if (!(ceiling > 0)) return null;
  const value = point != null ? Math.min(ceiling, Math.max(floor, point)) : (floor + ceiling) / 2;
  return { floor, ceiling, value: Math.round(value) };
};

// Whose numbers the profile's stored stats are (the about payload's
// statsProvider, performance_views.stats_provider): the league feed's
// (LEAGUE_STATS_PROVIDER), SportsRadar's, a partner team's own (its workbook
// or the games its admin entered), else null (no stats, football, an older
// payload). The Performance tab's attribution line reads it
// (athletePerformanceModel.performanceAttributed).
export const LEAGUE_STATS_PROVIDER = "cerebro";
const STATS_PROVIDERS = new Set([LEAGUE_STATS_PROVIDER, "sportsradar", "team"]);
export const statsProviderOf = (value) => (STATS_PROVIDERS.has(value) ? value : null);

// The profile's value module (D3) from the payload's valueBasis: only
// "performanceValue" switches to PerformanceValue; anything else, a missing
// field included, is CacheValue.
export const valueBasisOf = (basis) => (basis === "performanceValue" ? "performanceValue" : "cacheValue");

// The words the one value card shows, for each module. PerformanceValue
// takes CacheValue's slot with its own title, no badge and no (i): its
// range is the engine's, a less accurate CacheValue.
export const VALUE_CARD_WORDS = {
  cacheValue: {
    title: "CacheValue™",
    subtitle: "Market valuation estimate",
    paywallTitle: "Unlock CacheValue™ & CacheNILx™",
  },
  performanceValue: {
    title: "PerformanceValue™",
    subtitle: "Estimated from performance",
    paywallTitle: "Unlock PerformanceValue™ & CacheNILx™",
  },
};

// { kind, title, subtitle, paywallTitle, range, badge, badgeRank,
// badgeNote, tileSub }: the card for a basis. `tileSub` is the line under
// the phone tile's amount: CacheValue's badge, PerformanceValue's subtitle.
export const valueCardFor = (basis, { cacheValue = null, perfValue = null, valuePctLabel = null, valuePctRank = null, valuePctBasis = "" } = {}) => {
  const kind = valueBasisOf(basis);
  const words = VALUE_CARD_WORDS[kind];
  if (kind === "performanceValue") {
    return { kind, ...words, range: perfValue, badge: null, badgeRank: null, badgeNote: "", tileSub: words.subtitle };
  }
  return { kind, ...words, range: cacheValue, badge: valuePctLabel, badgeRank: valuePctRank, badgeNote: valuePctBasis || "", tileSub: valuePctLabel };
};

// ---------- box score lines ----------
// The nine box score tiles from `stat(label, performanceLabel)`: the
// number by its tile label, the stats doc's label as the second argument.
const boxScoreTiles = (stat) => [
  { label: "PPG", value: fmt1(stat("PPG", "Points")) },
  { label: "APG", value: fmt1(stat("APG", "Assists")) },
  { label: "RPG", value: fmt1(stat("RPG", "Rebounds")) },
  { label: "FG%", value: pctStr(stat("FG%", "Field Goal %")) },
  { label: "3P%", value: pctStr(stat("3P%", "Three Point %")) },
  { label: "FT%", value: pctStr(stat("FT%", "Free Throw %")) },
  { label: "SPG", value: fmt1(stat("SPG", "Steals")) },
  { label: "MPG", value: fmt1(stat("MPG", "Minutes")) },
  { label: "TO/G", value: fmt1(stat("TO/G", "Turnovers")) },
];

// Usage rate: possessions USED (shots + FT trips + turnovers) over an
// estimate of team possessions while on the floor (grassroots pace of ~70
// per 40 min x the athlete's minutes); null without minutes, shots or
// turnovers.
const usageRateOf = (shots, tov, mins) => {
  if (!mins || shots == null || tov == null) return null;
  const teamPoss = (70 * mins) / 40;
  return teamPoss > 0 ? Math.max(4, Math.min(45, ((shots + tov) / teamPoss) * 100)) : null;
};

// The shooting efficiency rows that have a number.
const shootingRows = (trueShooting, effectiveFg, usageRate) =>
  [
    {
      value: pctStr(trueShooting),
      label: "True shooting",
      desc: "Scoring efficiency across all shot types, with 2s, 3s, and free throws weighted together",
    },
    {
      value: pctStr(effectiveFg),
      label: "Effective FG%",
      desc: "FG% adjusted for the extra value of made 3-pointers, the truer shooting measure",
    },
    {
      value: pctStr(usageRate),
      label: "Usage rate",
      desc: "Share of team possessions used while on the floor, scoring volume in context",
    },
  ].filter((s) => s.value != null);

// ---------- play groups (school and grassroots, D6 / D7) ----------
// The groups a season's play falls in (users/profile_seasons.play_group)
// as the profile names them: utils/playGroups, the words Game by Game's
// season list prints too.
export { PLAY_GROUP_LABELS };
// The groups the box score switches between, in that one order. Other
// events join only when the peer group ranks them (their line has the
// ranks); camps never do.
const BOX_SCORE_GROUPS = PLAY_GROUP_ORDER.filter((key) => key !== "camp" && key !== "other");
const GROUP_ORDER = [...BOX_SCORE_GROUPS, "other"];

// A play group's name: the payload's label, else the profile's word.
export const playGroupLabel = (key, label) => textOf(label) || PLAY_GROUP_LABELS[textOf(key)] || "";

// A play group's season numbers (profile_seasons._line) by tile label.
const GROUP_STAT_KEYS = {
  PPG: "ppg",
  APG: "apg",
  RPG: "rpg",
  "FG%": "fgPct",
  "3P%": "threePct",
  "FT%": "ftPct",
  SPG: "spg",
  MPG: "mpg",
  "TO/G": "topg",
  "True shooting": "tsPct",
  "Effective FG%": "efgPct",
};

const unranked = (s) => ({ ...s, chips: [], nationalTop: null });
const textsOnce = (values) => [...new Set(values.map(textOf).filter(Boolean))];
const listUpTo = (items, max) => (items.length > max ? `${items.slice(0, max).join(", ")} and ${items.length - max} more` : items.join(", "));

// Whether the peer group's competition season is in a group: the group's
// season and one of its competitions, by the names the seasons store (the
// peer group is built from them, profile_analytics.current_competition).
const holdsPeerCompetition = (peers, row) => {
  const competition = textOf(peers?.competition);
  if (!competition) return false;
  const season = textOf(peers?.season);
  if (season && textOf(row?.season) && season !== textOf(row.season)) return false;
  return (Array.isArray(row?.competitions) ? row.competitions : []).some((c) => c && !c.camp && textOf(c.competition) === competition);
};

// A group's season total as a box score line: its numbers with no ranks
// (no peer group ranks them), its name for the subtitle ("Coastal League ·
// Harbor Prep School · 2026-27 · 4 games") and for the season summary's
// sentence ("Coastal League 2026-27", or "2025-26 grassroots play" for a
// group of several competitions).
function groupTotalLine(key, label, row) {
  const stats = row.stats && typeof row.stats === "object" ? row.stats : {};
  const totals = row.totals && typeof row.totals === "object" ? row.totals : {};
  const value = (tile) => num(stats[GROUP_STAT_KEYS[tile]]);
  const games = num(row.games) ?? num(totals.games);
  const totalGames = num(totals.games) || games;
  const fga = num(totals.fga);
  const fta = num(totals.fta);
  const shots = fga != null && fta != null && totalGames > 0 ? (fga + 0.44 * fta) / totalGames : null;
  const boxScore = boxScoreTiles(value).map(unranked);
  const shooting = shootingRows(value("True shooting"), value("Effective FG%"), usageRateOf(shots, value("TO/G"), value("MPG"))).map(unranked);
  const comps = (Array.isArray(row.competitions) ? row.competitions : []).filter((c) => c && typeof c === "object" && !c.camp);
  const competitions = textsOnce(comps.map((c) => c.competition));
  const teams = textsOnce(comps.map((c) => c.team));
  const season = textOf(row.season);
  const subtitleName = [
    listUpTo(competitions, 2),
    teams.length > 2 ? countOf(teams.length, "team") : teams.join(", "),
    season,
    games > 0 && countOf(games, "game"),
  ]
    .filter(Boolean)
    .join(" · ");
  const readName =
    competitions.length === 1 ? [competitions[0], season].filter(Boolean).join(" ") : [season, `${label.toLowerCase()} play`].filter(Boolean).join(" ");
  return {
    key,
    label,
    tag: "",
    ranked: false,
    season: season || null,
    boxScore,
    hasBoxScore: boxScore.some((s) => s.value != null),
    shooting,
    hasShooting: shooting.length > 0,
    hasPercentiles: false,
    hasStateRanks: false,
    gamesPlayed: games > 0 ? games : null,
    lineName: readName || null,
    boxScoreLine: null,
    subtitleName: subtitleName || label,
    showsGroupLine: true,
  };
}

// The academic season the stats doc's line counts (about/'s
// cerebroSeasonYear, as profile_seasons.normalize_season_label reads it): a
// range ("2026-27", "2026/2027") by the year it starts; a lone year as the
// season on file it names. A league feed counts calendar years (2026 is
// 2025-26) and a college feed names a season by the year it starts (2025
// is 2025-26), so a year reads as the season on file ending in it, else the
// one starting in it. null when it names no season.
const seasonFrom = (start) => `${start}-${String((start + 1) % 100).padStart(2, "0")}`;
const statsSeasonOf = (ci) => {
  const value = ci?.cerebroSeasonYear;
  const raw = typeof value === "number" || typeof value === "string" ? String(value).trim() : "";
  const range = /^(\d{4})\s*[-/]\s*(\d{2}|\d{4})$/.exec(raw);
  if (range) return seasonFrom(Number(range[1]));
  if (!/^\d{4}$/.test(raw)) return null;
  const year = Number(raw);
  const onFile = (Array.isArray(ci.seasons) ? ci.seasons : []).map((s) => textOf(s?.label));
  return [seasonFrom(year - 1), seasonFrom(year)].find((label) => onFile.includes(label)) || null;
};

// The box score's lines by play group and the one it opens on (D7), from
// the payload's playGroups [{group, label, season, games, stats, totals,
// competitions, ranked}] and defaultGroup. `peerLine` is the peer group's
// line when one ranks the athlete (groupLine), `today` the box score's own
// line (the peer group's, else the stats doc's). The group the peer group
// ranks (its `ranked` flag; a payload without one: the group holding the
// peer group's competition) shows `today`, its numbers and ranks; another
// its season total. With one group (no switch) that group shows `today`
// and tags it when `today` is its line; when `today` has no number at all,
// its own season total, tagged. {playGroups: [], defaultGroup: null}
// without a group to show.
function buildPlayGroups(ci, peerLine, today) {
  const peers = ci.peers && typeof ci.peers === "object" ? ci.peers : null;
  const rows = (Array.isArray(ci.playGroups) ? ci.playGroups : []).filter((g) => g && typeof g === "object" && GROUP_ORDER.includes(textOf(g.group)));
  const claims = (row) => (typeof row.ranked === "boolean" ? row.ranked : holdsPeerCompetition(peers, row));
  const firstOf = (key, test = () => true) => rows.find((row) => textOf(row.group) === key && test(row)) || null;
  const rankedRow = peerLine ? GROUP_ORDER.map((key) => firstOf(key, claims)).find(Boolean) || null : null;
  const shown = GROUP_ORDER.map((key) => (key === "other" ? (rankedRow && textOf(rankedRow.group) === "other" ? rankedRow : null) : firstOf(key))).filter(Boolean);
  if (!shown.length) return { playGroups: [], defaultGroup: null };

  if (shown.length === 1) {
    const [row] = shown;
    const key = textOf(row.group);
    const label = playGroupLabel(key, row.label);
    const season = textOf(row.season) || null;
    const one = (line) => ({ playGroups: [line], defaultGroup: key });
    // The subtitle names the group, other events too, as the competition
    // lines do (D7: one group, no switch, the tag in the subtitle).
    const tag = label;
    // A line with no number on it (no stats doc line, no peer line) gives
    // way to the group's own season total, named and tagged, so the box
    // score never reads "no season stats" beside a season on file.
    if (!today.hasBoxScore) {
      const total = groupTotalLine(key, label, row);
      if (total.hasBoxScore) return one({ ...total, tag, subtitleName: [...new Set([total.subtitleName, tag].filter(Boolean))].join(" · ") });
    }
    // `today` is the group's line when the peer group ranks the group, or,
    // with no peer line, when the stats doc counts the group's season.
    // Otherwise it is another season's play (an athlete whose newest season
    // is other events after a season of school play) or the line of a peer
    // group no group holds: no group names it, and the box score keeps its
    // own line, untagged, as before play groups.
    const own = row === rankedRow || (!peerLine && season !== null && statsSeasonOf(ci) === season);
    if (!own) return { playGroups: [], defaultGroup: null };
    return one({ ...today, key, label, season, subtitleName: null, tag, ranked: row === rankedRow });
  }

  const lineOf = (row) => {
    const key = textOf(row.group);
    const label = playGroupLabel(key, row.label);
    const base = { key, label, season: textOf(row.season) || null, subtitleName: null };
    if (row === rankedRow) return { ...today, ...base, tag: "", ranked: true };
    return groupTotalLine(key, label, row);
  };
  const playGroups = shown.map(lineOf);

  const keys = playGroups.map((g) => g.key);
  const wanted = textOf(ci.defaultGroup);
  // The group the peer group ranks, else the one with the newest season.
  const newest = [...shown].sort((a, b) => textOf(b.season).localeCompare(textOf(a.season)))[0];
  const fallback = textOf((rankedRow || newest).group);
  const defaultGroup = keys.includes(wanted) ? wanted : keys.includes(fallback) ? fallback : keys[0];
  return { playGroups, defaultGroup };
}

export function buildProfileModel(athlete = {}, cacheInformation = {}, codeMap = {}, circleMembers = [], options = {}) {
  const ci = cacheInformation || {};
  const cerebro = ci.cerebroBio || {};
  const perf = ci.performance || {};
  const about = ci.aboutAthlete || {};
  const P = (label) => num(perf[label]);

  // ---------- identity / header ----------
  const firstName = athlete.name || "";
  const lastName = athlete.surname || "";
  const fullName = `${firstName} ${lastName}`.trim() || "Athlete";
  // The codes table's name of the sport ("Basketball"): the competition
  // tiers pick their tables by it.
  const sportName = codeMap.sport?.[athlete.primarySport]?.name || "";
  // Backend-shared identity (athlete_identity.py) so this page and the
  // public share page derive teams, schools and hometown from ONE
  // precedence; the local lookups below are only the legacy fallback.
  const identity = ci.identity || athlete.identity || {};
  // The athlete's gender (1 men, 2 women, 0 unknown): their own, else the
  // one their teams' leagues say (competeGender). The sport as the profile
  // names it follows it (Kobi, 2026-10-07: a women's college basketball
  // profile read "College · Basketball"): "Women's basketball" for a woman
  // in a sport whose men's and women's games are apart; a man, an unknown
  // gender and any other sport keep the codes table's name. The meta
  // chips, the header, the sport tab and the CacheValue subline all read it.
  const gender = competeGender(athlete, identity, sportName);
  const sport = sportLabelFor(sportName, gender);
  // The level the profile reads: the identity's (a graduated senior on a
  // college team reads as College), else the stored one.
  const lvl = Number(identity.effectiveLevel) || Number(athlete.levelOfPlay) || 0;
  const level = LEVEL_NAMES[lvl] || "";

  const positionVal = Array.isArray(athlete.position) ? athlete.position[0] : athlete.position;
  let positionName = codeMap.position?.[positionVal]?.name || cerebro.position || "";
  if (/unknown/i.test(positionName)) positionName = "";

  // Registration and the onboarding details step store 0 for "no jersey
  // yet", the way the wizard and the roster claim read it, so 0 and blank
  // show no chip instead of "#0".
  const jerseyRaw = athlete.jerseyNumber == null ? "" : String(athlete.jerseyNumber).trim();
  const jersey = jerseyRaw && jerseyRaw !== "0" ? jerseyRaw : null;
  // An identity from before the slots (older payloads) fills a missing
  // school or club from the codes and the bio, as the page did before the
  // backend derived them.
  const teamsIdentity = identity.current
    ? identity
    : {
      ...identity,
      school: identity.school || codeMap.school?.[athlete.previousSchool]?.name || cerebro.high_school || cerebro.pre_draft_team || "",
      club: identity.club || ci.cachePartnerTeam?.name || codeMap.team?.[athlete.currentTeam]?.name || cerebro.current_team?.name || "",
    };
  const slots = identitySlots(teamsIdentity, lvl);
  // "City, ST" from the backend identity; the utils/hometown twin applies
  // the same rules when the identity block is missing (older payloads).
  const localHometown = identity.hometown ? null : hometownParts(athlete, codeMap, cerebro);
  const hometown = identity.hometown || localHometown?.text || "";
  // The state (postal code) for the CacheValue subline.
  const stateName =
    identity.hometownState ||
    (identity.hometown ? splitHometown(identity.hometown).state : localHometown?.state) ||
    "";

  // Each school and team shows once, under what it is: College (a pro's
  // college is their University), High school (past high school, the last
  // one), Club, and Team for a pro's team. The backend matched them by
  // identity, so nothing is compared by name here.
  const metaChips = [
    sport && { label: sport },
    jersey != null && { label: `#${jersey}` },
    positionName && { label: `Pos: ${positionName}` },
    slots.college && { label: `College: ${slots.college}` },
    slots.university && { label: `University: ${slots.university}` },
    slots.highSchool && { label: `High school: ${slots.highSchool}` },
    slots.club && { label: `Club: ${slots.club}` },
    slots.proTeam && { label: `Team: ${slots.proTeam}` },
    hometown && { label: `Hometown: ${hometown}` },
  ]
    .filter(Boolean)
    .map((c) => c.label);
  // The one place the header subtitle names: the current institution, else
  // the club or team (never a college athlete's last high school).
  const headline = slots.college || slots.university || (lvl >= 2 ? "" : slots.highSchool) || slots.club || slots.proTeam || "";

  // ---------- CacheScore + contributions ----------
  const cacheScore = num(ci.cacheScore?.value);
  const confRaw = (ci.cacheScore?.confidenceFactor || "").toString();
  const confidence = confRaw ? confRaw.charAt(0).toUpperCase() + confRaw.slice(1).toLowerCase() : null;
  // The pillars on 0 to 100, as the payload sends them (the cards read them
  // out of 10, utils/indexScore). A pillar the payload does not carry is
  // null, so it shows the en dash rather than a 0 it never scored.
  const contrib = ci.cacheScoreContribution || {};
  const contributions = {
    performance: num(contrib.gameImpact),
    visibility: num(contrib.onlineImpact),
    offCourt: num(contrib.irlImpact),
    hasData: contrib.gameImpact != null || contrib.onlineImpact != null || contrib.irlImpact != null,
  };

  // ---------- valuation ----------
  const buildRange = (obj) => {
    if (!obj) return null;
    const min = num(obj.min);
    const max = num(obj.max);
    const from = num(obj.from);
    const to = num(obj.to);
    if (min == null && max == null && from == null && to == null) return null;
    const floor = min ?? from ?? 0;
    const ceiling = max ?? to ?? floor;
    const value =
      from != null && to != null && to >= from
        ? Math.round((from + to) / 2)
        : Math.round((floor + ceiling) / 2);
    return { floor, ceiling, value };
  };
  const cacheValue = buildRange(ci.cacheValue);
  const cacheNilx = buildRange(ci.cacheShare);
  const nilxTier = ci.cacheShare?.tier || null;
  // PerformanceValue™ (J2): the engine's value from the performance score,
  // with visibility and off-court estimated.
  const perfValue = performanceRange(ci.performanceValue);
  // ONE value module per profile (Jayson's 10/21 note, D3): an onboarded
  // athlete's is CacheValue; an athlete ported from a data source (a pilot,
  // a sign-up that never finished onboarding) shows PerformanceValue in the
  // same slot, a less accurate CacheValue. The about payload says which
  // (valueBasis, the server's onboarded rule); a payload without it means
  // CacheValue, so an older payload never shows PerformanceValue.
  const valueBasis = valueBasisOf(ci.valueBasis);

  // ---------- NILx rate card (design's 6 tiles) ----------
  // Backend supplies 6 canonical rates (events, socialPost, reel, story,
  // longVideo, share) — all real, per the Summer 2026 rate-card update.
  // Guarded categories arrive as null and their tile is simply omitted.
  // reelStory is the pre-update key, kept as a fallback for stale payloads.
  const nr = ci.nilRates || {};
  const nilRates = [
    { label: "Events", value: num(nr.events) },
    { label: "Social Media Post", value: num(nr.socialPost) },
    { label: "Reel / TikTok", value: num(nr.reel ?? nr.reelStory) },
    { label: "Story", value: num(nr.story ?? nr.reelStory) },
    { label: "Long form video", value: num(nr.longVideo) },
    { label: "Repost", value: num(nr.share) },
  ].filter((r) => r.value != null);

  // ---------- profile block ----------
  // The athlete's own height and weight first (what they typed or
  // confirmed), the bio's only for a blank one. 0 is the registration
  // default, not a value, so it falls through; no row when neither is set.
  // Weight is asked of men only (Kobi, 2026-10-07: "Weight (males only)"): a
  // bio weight shows for a man only, never for a woman or a profile with no
  // gender yet; a weight the athlete entered is always theirs to show.
  const heightStr = inchesToFeet(num(athlete.height) || num(cerebro.height_inches));
  const weightVal = num(athlete.weight) || (isMale(athlete) ? num(cerebro.weight_lb) : null) || null;
  // The graduation year is the high school one (what Cerebro has, for
  // college athletes too); a college athlete's year is the college level.
  // The athlete's own entry wins, as in the edit hub and the coach roster;
  // Cerebro's grad year (else its draft-eligible year) only fills a blank.
  const gradYear = yearOf(athlete.gradYear) || yearOf(cerebro.grad_year) || yearOf(cerebro.draft_eligible_year);
  // A level on file shows as its code (the header subtitle's too); an
  // estimate from the class year only in About, marked as one.
  const { code: collegeLevel, text: collegeLevelText } = collegeLevelShown(identity, athlete, cerebro, lvl);

  const profileFields = [
    heightStr && { label: "Height", value: heightStr },
    weightVal != null && { label: "Weight", value: `${weightVal}`, unit: "lbs" },
    slots.college && { label: "College", value: slots.college },
    slots.university && { label: "University", value: slots.university },
    slots.highSchool && { label: "High school", value: slots.highSchool },
    slots.club && { label: "Club", value: slots.club },
    slots.proTeam && { label: "Team", value: slots.proTeam },
    collegeLevelText && { label: "College level", value: collegeLevelText },
    gradYear && { label: "High school graduation year", value: `${gradYear}` },
    hometown && { label: "Hometown", value: hometown },
    // Team history: earlier colleges, high schools and clubs, as lists.
    slots.previousColleges.length > 0 && {
      label: lvl === 3 ? "Previous universities" : "Previous colleges",
      value: slots.previousColleges.join(", "),
    },
    slots.previousHighSchools.length > 0 && { label: "Previous high schools", value: slots.previousHighSchools.join(", ") },
    slots.previousClubs.length > 0 && { label: "Previous clubs", value: slots.previousClubs.join(", ") },
  ].filter(Boolean);

  const bio = about.bio || "";

  // Every circle member is listed on the profile's Circle card, but only
  // the athlete's designated points of contact (contact === true, max 2)
  // expose an email — everyone else renders without the mailto envelope.
  // The same members carry the phone card's "Primary" badge (primary).
  const circle = (circleMembers || []).map((m) => ({
    role: m.relationship || "Circle member",
    name: m.name || m.email || "",
    email: m.contact === true ? (m.email || "") : "",
    primary: m.contact === true,
  }));

  // ---------- real percentiles (the peer group + its same-state subset) ----------
  const analytics = ci.analytics || {};
  const nat = analytics.national || {};
  const conf = analytics.conference || {};
  const standIn = statsAreStandIn(ci, athlete);
  // Whose ranks they are (Kobi, 2026-10-07, on a college freshman whose
  // stats stand in from their high school, asking whether national details
  // belong along the box score). Stand-in stats rank the
  // competition of the record they come from, and a group the backend puts
  // at another level (analytics.peerLevel) ranks that level: those ranks
  // name their group ("Top 3% in Nevada 5A 2026", "Top 2% in NV"), and
  // with no name for the group they stay off the card rather than read as
  // national or college ones. The athlete's own group keeps the plain
  // "Top N%" (peerRankScope).
  const rankScope = peerRankScope({
    standIn,
    peerLevel: analytics.peerLevel,
    level,
    peerLabel: textOf(analytics.peerLabel) || textOf(ci.peers?.label),
    stateCode: ci.peers?.state?.code,
  });
  // Blue = the same-state subset of the peer group (the "State" chips),
  // grey = the whole group (the "Peer group" chips). Each chip only
  // appears when the backend produced a real percentile for that stat.
  const chipsFor = (label) => {
    if (rankScope.hidden) return [];
    const out = [];
    if (conf[label] != null) {
      if (!rankScope.foreign) out.push({ label: `Top ${conf[label]}%`, variant: "blue" });
      else if (rankScope.state) out.push(namedRank(conf[label], rankScope.state, "blue"));
    }
    if (nat[label] != null) {
      out.push(rankScope.foreign ? namedRank(nat[label], rankScope.group, "gray") : { label: `Top ${nat[label]}%`, variant: "gray" });
    }
    return out;
  };

  // ---------- the line the box score shows ----------
  // Every rank describes the number beside it, so the box score shows the
  // line its percentiles rank. A live peer group ranks the athlete's line
  // in the group's competition season (the payload's `peers.athlete`, 19.0
  // PPG in Nike EYBL 16U 2026): the tiles, the shooting rows, the radar and
  // the season summary read that line, and the box score names it. A stat
  // the line lacks shows none, never the aggregate's. The cohort snapshot
  // (it ranks the stats doc itself) and no group keep `performance`, the
  // scoring aggregate (the calendar year of the newest game, every
  // competition).
  const group = groupLine(ci.peers, analytics.peerSource, ci.seasons);
  const stat = (label, perfLabel) => (group ? num(group.line[label]) : P(perfLabel));
  // A stat's rank chips and the N of its peer group "Top N%" (for the season
  // summary, which names the group), only beside a number and never for a
  // group the card cannot name.
  const withRanks = (s) => ({
    ...s,
    chips: s.value == null ? [] : chipsFor(s.label),
    nationalTop: s.value == null || rankScope.hidden ? null : num(nat[s.label]),
  });

  // ---------- season box score (that line + its percentile chips) ----------
  const boxScore = boxScoreTiles(stat).map(withRanks);
  const hasBoxScore = boxScore.some((s) => s.value != null);
  // The games in that line: the group's competition season's (null when
  // the seasons on file do not show it), else the aggregate's.
  const gamesPlayed = group ? group.games : num(P("Total Games Played"));
  // The line by name for the box score header, "Nike EYBL 16U 2026, 17
  // games"; null when the box score shows the aggregate.
  const lineName = group ? group.name : null;
  const boxScoreLine = group ? [group.name, group.games > 0 && countOf(group.games, "game")].filter(Boolean).join(", ") || null : null;
  // True when the tiles show the group's line rather than `performance`:
  // the stored AI season summary, written from the scoring aggregate, then
  // quotes other numbers than the tiles (profileNarratives).
  const showsGroupLine = !!group;

  // ---------- shooting efficiency (real advanced metrics + chips) ----------
  // Usage rate: possessions USED (shots + FT trips + turnovers) over an
  // estimate of team possessions while on the floor (grassroots pace of
  // ~70 per 40 min x the athlete's minutes). Cerebro's Possessions field IS
  // possessions used, so dividing by it reads ~100% by construction. The
  // group's line has no attempts, but its true shooting gives them: TS =
  // PTS / (2 x (FGA + 0.44 FTA)), so FGA + 0.44 FTA = PPG / (2 x TS).
  const trueShooting = stat("True shooting", "True Shooting %");
  let shots = null;
  if (group) {
    const ppg = num(group.line.PPG);
    if (ppg != null && trueShooting > 0) shots = (ppg * 100) / (2 * trueShooting);
  } else {
    const fga = num(P("Field Goals Attempted"));
    const fta = num(P("Free Throws Attempted"));
    if (fga != null && fta != null) shots = fga + 0.44 * fta;
  }
  const usageRate = usageRateOf(shots, stat("TO/G", "Turnovers"), stat("MPG", "Minutes"));

  const shooting = shootingRows(trueShooting, stat("Effective FG%", "Effective FG %"), usageRate).map(withRanks);
  const hasShooting = shooting.length > 0;
  // The rank legend's entries: the ranks that show.
  const shownRanks = [...boxScore, ...shooting].flatMap((s) => s.chips);
  const hasPercentiles = shownRanks.length > 0;
  const hasStateRanks = shownRanks.some((c) => c.variant === "blue");

  // ---------- player radar (the peer group's percentiles, Figma 1504:5385) ----------
  // The league spokes (radarSpokes): each detail names the number its box
  // score tile shows, the one its percentile ranks.
  const shown = Object.fromEntries([...boxScore, ...shooting].map((s) => [s.label, s.value]));
  // No shape for a group the card cannot name, as no chips.
  const radar = rankScope.hidden ? null : radarSpokes(nat, (key) => shown[key]);
  // The group those percentiles rank against, as the backend names it
  // (league or cohort, "Nike EYBL 16U 2026, all positions"); otherwise the
  // athlete's own league. The league group only: college and pro have their
  // own captions in radarGroups.
  // A group the athlete is not in now never falls back to "your league":
  // its ranks name it by the label peerRankScope read (the payload's peers
  // label when analytics has none), so the subtitle and the caption do too.
  const peerLabel =
    (typeof analytics.peerLabel === "string" && analytics.peerLabel.trim()) ||
    (rankScope.foreign && textOf(ci.peers?.label)) ||
    (options.selfView ? "players in your league" : "players in their league");
  // Every group the radar can show, League first: today's peer group, then
  // the college and pro players the backend compared the athlete with (the
  // payload's `comparisons`), each a group the athlete's level sees. The
  // shot chart is gone at every level: its dots were generated, not shots.
  // A peer group of another level takes its level's name for a tab ("High
  // school" on a college profile), never "League".
  const radarGroups = buildRadarGroups(ci.comparisons, level, radar, peerLabel, rankScope.levelName);

  // ---------- scoring breakdown (real, from made 2s/3s/FTs) ----------
  const threePM = num(P("Three Points Made"));
  let twoPM = num(P("Two Points Made"));
  const fgm = num(P("Field Goals Made"));
  if (twoPM == null && fgm != null && threePM != null) twoPM = fgm - threePM;
  const ftm = num(P("Free Throws Made"));
  let scoringBreakdown = null;
  if (twoPM != null && threePM != null && ftm != null) {
    const pts2 = twoPM * 2;
    const pts3 = threePM * 3;
    const ptsFt = ftm;
    const totalPts = pts2 + pts3 + ptsFt;
    if (totalPts > 0) {
      scoringBreakdown = [
        { label: "2-pointers", points: pts2, share: Math.round((pts2 / totalPts) * 100), color: "#2196F3" },
        { label: "3-pointers", points: pts3, share: Math.round((pts3 / totalPts) * 100), color: "#FF4400" },
        { label: "Free throws", points: ptsFt, share: Math.round((ptsFt / totalPts) * 100), color: "#4CAF50" },
      ];
    }
  }

  // ---------- current season (display seasons, peer group, teammates) ----------
  // The about payload's seasons (academic years, newest first), the peer
  // group behind the comparison and the teammate comparison; null without
  // a season on file.
  const currentSeason = buildCurrentSeason(ci, firstName, rankScope);

  // ---------- school and grassroots lines (Kobi's 10/21 note, D7) ----------
  // The box score's line per play group, from the about payload's
  // playGroups (each group's newest season with games): School, Grassroots,
  // College, Pro. The group the peer group ranks keeps the line above, its
  // numbers and ranks; any other shows its season total with no ranks. One
  // group keeps the line above and names its group; none (an older payload,
  // an athlete with camps and other events only) changes nothing.
  // CacheScore and CacheValue stay combined: this is display only.
  const { playGroups, defaultGroup } = buildPlayGroups(ci, group, {
    boxScore,
    hasBoxScore,
    shooting,
    hasShooting,
    hasPercentiles,
    hasStateRanks,
    gamesPlayed,
    lineName,
    boxScoreLine,
    showsGroupLine,
  });

  // Shot-zone heatmap seed: no shot coordinates exist in the data, so the
  // court chart is a design-faithful visual biased by the athlete's REAL
  // scoring mix (paint-heavy vs perimeter) and efficiency. No profile draws
  // that chart any more (its dots are generated, not shots); the seed stays
  // for the kept ShotZoneChart.
  const shotProfile = {
    twoShare: scoringBreakdown ? scoringBreakdown[0].share : 50,
    threeShare: scoringBreakdown ? scoringBreakdown[1].share : 30,
    seed: Math.round((cacheScore || 60) * 100 + (num(P("Points")) || 0) * 7),
    efficiency: num(P("True Shooting %")),
  };

  // ---------- visibility footprint (real platform metrics) ----------
  const pm = ci.socialScore?.platform_metrics || {};
  const platformDefs = [
    { key: "instagram", name: "Instagram" },
    { key: "tiktok", name: "TikTok" },
    { key: "x", name: "X" },
    { key: "youtube", name: "YouTube" },
  ];
  const platforms = platformDefs.map((d) => {
    const m = pm[d.key];
    const followers = num(m?.followers);
    return {
      key: d.key,
      name: d.name,
      linked: followers != null && followers > 0,
      followers,
      engagement: num(m?.avg_engagement_rate),
      verified: !!m?.verified,
    };
  });
  const engagement = num(about.engagementRate ?? ci.socialScore?.avg_engagement_rate);
  const totalFollowers = num(about.totalFollowers ?? ci.socialScore?.total_followers);
  // The about payload sends a 0 rate when none is measured, so a 0 with
  // nothing linked is not visibility data.
  const hasVisibility = platforms.some((p) => p.linked) || engagement > 0;

  // ---------- brand category alignment (derived from real audience signals) ----------
  const brand = deriveBrand(totalFollowers, engagement, platforms, sportName);

  // Real national CacheValue distribution (the athlete's level, sport and
  // gender) when the backend has enough stored valuations: {p10, median,
  // p90, size, gender, genderKnown, basis}.
  const band = analytics.valueBand || null;
  // The CacheValue badge ranks the CacheValue beside it: its place in that
  // distribution (the card's "National median"), the top 10% from the 90th
  // percentile up and the top half from the median up. None without the
  // distribution or below its median: never the peer group's PPG rank (one
  // league season, not a valuation) and never a CacheScore tier.
  const valueP90 = num(band?.p90);
  const valueMedian = num(band?.median);
  const youValue = cacheValue ? cacheValue.value : null;
  const valueTop =
    youValue == null
      ? null
      : valueP90 != null && youValue >= valueP90
        ? 10
        : valueMedian != null && youValue >= valueMedian
          ? 50
          : null;
  // The group the distribution is of, as the backend names it ("College
  // women's basketball"; "College basketball" when it pooled men and women).
  const bandBasis = textOf(band?.basis);
  // "Nationally" only for a cohort of the athlete's own game: one that
  // pooled men's and women's valuations (the backend knew no gender,
  // genderKnown false) is no national rank in it, so the badge says "Top
  // 10%" and names the group beside it ("Top 10% · College basketball"). A
  // payload from before genderKnown reads as before. `valuePctRank` and
  // `valuePctBasis` are the two parts, for a card that sets them apart.
  const valuePooled = band?.genderKnown === false;
  const valuePctRank = valueTop == null ? null : `Top ${valueTop}%${valuePooled ? "" : " nationally"}`;
  const valuePctBasis = valuePctRank && valuePooled ? bandBasis : "";
  const valuePctLabel = valuePctBasis ? `${valuePctRank} · ${valuePctBasis}` : valuePctRank;
  // The one value card the profile shows (D3), with its words: CacheValue
  // with its badge, or PerformanceValue in the same slot with none. A
  // missing PerformanceValue reads "Not available yet", never CacheValue.
  const valueCard = valueCardFor(valueBasis, { cacheValue, perfValue, valuePctLabel, valuePctRank, valuePctBasis });

  // ---------- CacheValue in context (real range + national percentile) ----------
  // The percentile that picks the competition tiers: the peer group's PPG
  // rank when it ranks the athlete at their level now (not stand-in stats,
  // not a group of another level), else the CacheScore band
  // (competePercentile).
  const pctNum = competePercentile({
    national: nat,
    peerLevel: analytics.peerLevel,
    level,
    standIn,
    cacheScore,
  });
  // The national distribution (`band`, above) when there is one; the
  // athlete's own ±15% band is the fallback. Floor/ceiling stretch to keep
  // the athlete inside the band. A CacheValue distribution: none for a
  // profile whose value module is PerformanceValue (D3), so no (i) opens it.
  let context = null;
  if (cacheValue && valueBasis === "cacheValue") {
    const you = cacheValue.value;
    const floor = band ? Math.min(band.p10, cacheValue.floor) : cacheValue.floor;
    const ceiling = band ? Math.max(band.p90, cacheValue.ceiling) : cacheValue.ceiling;
    const median = band
      ? band.median
      : Math.round(floor + (ceiling - floor) * 0.36);
    context = {
      floor,
      median: Math.max(floor, Math.min(ceiling, median)),
      you,
      ceiling,
      gapToCeiling: Math.max(0, ceiling - you),
      // "College · Women's basketball · G · NV": the sport as the profile
      // names it.
      subline: [level, sport, positionName, stateName].filter(Boolean).join(" · "),
      percentile: valuePctLabel,
      percentileRank: valuePctRank,
      percentileBasis: valuePctBasis,
      // By sport, gender and level: a women's profile never reads the NBA.
      comparePills: competeTiers(sportName, gender, level, pctNum),
      medianIsReal: !!band,
      // The group the distribution is of, for the median's label
      // ("National median · College women's basketball"); "" without one.
      basis: band ? bandBasis : "",
      // An anchor names its percentile only when it shows it: the bar
      // stretches to the athlete's own range where that reaches past the
      // distribution, and without one the anchors are that range.
      floorIsP10: !!band && floor === band.p10,
      ceilingIsP90: !!band && ceiling === band.p90,
      // The distribution's 10th and 90th percentiles, ticked on the bar
      // whenever there is a distribution (D5), also where the bar stretches
      // past them to the athlete's own range; null without one.
      p10: band ? num(band.p10) : null,
      p90: band ? num(band.p90) : null,
    };
  }

  // ---------- Cache AI (real backend summaries) ----------
  const performanceSummary = about.summaryOfThePerformanceAnalytics || "";
  const engagementSummary = about.summaryOfTheEngagementAnalytics || "";

  return {
    firstName,
    lastName,
    fullName,
    avatar: athlete.avatar,
    athleteId: ci.athleteId || athlete.athleteId || athlete._id,
    socialMedia: athlete.socialMedia || [],
    collegeLevel,
    // The high school graduation year About shows (the athlete's own first),
    // for the other places that show the class year.
    gradYear,
    sport,
    level,
    // The level as a number (1 HS, 2 College, 3 Pro, 0 none) and the place
    // the header subtitle names.
    levelOfPlay: lvl,
    headline,
    // "Stats from Lincoln High, 2025-26" when the stats stand in for the
    // team feed, else "": the box score says whose stats it shows.
    statsStandIn: statsStandInLabel(ci.statsStandIn),
    // Every record the CacheScore's game stats count, with its games ("Stats
    // from Oakland Soldiers 16U, 22 games · Brentwood School, 0 games"), and
    // the notice while the stats are not live ("Stats as of Oct 12, 2026.
    // Live updates are paused."); "" for either when there is nothing to say.
    statsSources: statsSourcesLine(ci.statsSources, { standIn: ci.statsStandIn }),
    statsStale: statsStaleNotice(ci.statsStatus),
    // Whose numbers those stats are ("cerebro", "sportsradar", "team" or
    // null): the Performance tab's attribution line shows only over the
    // league feed's.
    statsProvider: statsProviderOf(ci.statsProvider),
    positionName,
    stateName,
    metaChips,

    cacheScore,
    confidence,
    contributions,

    cacheValue,
    cacheNilx,
    nilxTier,
    perfValue,
    // "cacheValue" | "performanceValue": the profile's one value module, and
    // that card's words and range (D3).
    valueBasis,
    valueCard,
    valuePctLabel,
    valuePctRank,
    valuePctBasis,
    nilRates,

    bio,
    profileFields,
    circle,
    // True on the athlete's own dashboard ("you/your" voice); false for
    // business/coach/brand viewers (third-person, athlete's name).
    selfView: !!options.selfView,

    boxScore,
    hasBoxScore,
    hasPercentiles,
    hasStateRanks,
    gamesPlayed,
    lineName,
    boxScoreLine,
    showsGroupLine,
    shooting,
    hasShooting,
    // The box score's lines by play group ([] or one per group, D7) and the
    // one it opens on.
    playGroups,
    defaultGroup,
    radar,
    peerLabel,
    radarGroups,
    scoringBreakdown,
    shotProfile,
    currentSeason,

    platforms,
    engagement,
    totalFollowers,
    hasVisibility,
    brand,
    context,

    performanceSummary,
    engagementSummary,

    // Jayson's performance cards (the about payload's performanceV2
    // {available}, worked out from the stored stats doc): true when the
    // Performance section loads them from business/athletes/about/
    // .../performance/; false keeps the profile's own cards.
    performanceV2: ci.performanceV2?.available === true,
  };
}

// ---------- player radar ----------
// One spoke per stat with a percentile, clockwise from the top in this
// order. A spoke plots 100 minus N for "Top N%", the share of the group
// below the athlete, so the outer ring is the top of the group. Fewer than
// three spokes draw no shape: null. `numberOf(stat)` is the number a
// spoke's detail names, the one its percentile ranks (null: the rank alone).
const RADAR_AXES = [
  { key: "PPG", unit: "PPG" },
  { key: "APG", unit: "APG" },
  { key: "RPG", unit: "RPG" },
  { key: "SPG", unit: "SPG" },
  { key: "True shooting", unit: "true shooting", percent: true },
  { key: "3P%", unit: "from three", percent: true },
];

function radarSpokes(percentiles, numberOf) {
  const spokes = RADAR_AXES.filter((a) => num(percentiles?.[a.key]) != null).map(({ key, unit }) => {
    const top = num(percentiles[key]);
    const value = numberOf(key);
    return {
      key,
      label: key,
      value: Math.max(0, Math.min(100, 100 - top)),
      detail: value != null ? `${value} ${unit}, top ${top}%` : `Top ${top}%`,
      rank: `Top ${top}%`,
    };
  });
  return spokes.length >= 3 ? spokes : null;
}

// The radar's groups (the Figma note on 1504:5385: HS athletes compared
// with college and pro players): League, today's peer group, then College
// and Pro from the about payload's `comparisons` (users/profile_comparisons.py).
// HS athletes see all three, college athletes League and Pro, pro athletes
// League only; a group needs three ranked stats. A level group's caption
// names its pool ("NCAA D-I and D-II players") and nothing else: the x0.85
// per-level factor applies to the final score, not to individual stats
// (Jayson, 10/2), so the spokes read the athlete's own numbers and no
// caption names a scale, even when an older payload still sends one.
const GROUP_TABS = { league: "League", college: "College", pro: "Pro" };
const GROUPS_BY_LEVEL = { HS: ["league", "college", "pro"], College: ["league", "pro"], Pro: ["league"] };

// `leagueLevelName` names the league group's tab when the group is of
// another level than the athlete ("High school" for a college athlete's
// stand-in high school group), unless another tab already reads so.
function buildRadarGroups(comparisons, level, leagueRadar, leaguePeerLabel, leagueLevelName = "") {
  const shown = GROUPS_BY_LEVEL[level] || ["league"];
  const groups = [];
  if (leagueRadar && shown.includes("league")) {
    groups.push({ key: "league", tab: GROUP_TABS.league, radar: leagueRadar, peerLabel: leaguePeerLabel });
  }
  ["college", "pro"]
    .filter((key) => shown.includes(key))
    .forEach((key) => {
      const group = levelGroup(key, comparisons?.[key]);
      if (group) groups.push(group);
    });
  const league = groups.find((g) => g.key === "league");
  if (league && leagueLevelName && !groups.some((g) => g.tab === leagueLevelName)) league.tab = leagueLevelName;
  return groups;
}

function levelGroup(key, comparison) {
  if (!comparison || typeof comparison !== "object") return null;
  const line = comparison.athlete || {};
  const radar = radarSpokes(comparison.percentiles || {}, (stat) => {
    const value = num(line[stat]);
    return RADAR_AXES.find((a) => a.key === stat)?.percent ? pctStr(value) : fmt1(value);
  });
  const label = textOf(comparison.label);
  if (!radar || !label) return null;
  return { key, tab: GROUP_TABS[key], radar, peerLabel: label };
}

// ---------- current season ----------
// The Current Season card's data, from the about payload (the P2 season
// contract): the seasons on file, newest first, each with its competition
// lines, numbers and bars; the peer comparison and the teammate comparison,
// each tied to the one season it covers (the season the payload names when
// it is on file, else the season shown first: the cohort snapshot names
// none). The card picks the season; profileNarratives.seasonFor decides
// what shows with it.
const SEASON_KINDS = ["games", "events", "none"];
const RESULT_WORDS = { W: "win", L: "loss", T: "tie" };
const textOf = (v) => (typeof v === "string" ? v.trim() : "");
const countOf = (n, word) => `${n} ${word}${n === 1 ? "" : "s"}`;

// Camps and all-star games (Kobi and Jayson's 10/2 notes on JJ's Pangos
// camp) stay on the timeline, in date order, but out of the season's
// numbers and of performance scoring: their one-off rosters play very
// differently from normal competition. The backend names them
// (users/competition_kinds.py): `camp` and `campLabel` on a competition line
// and a chart point, `campGames` on a season. A row's tag is its label
// ("Camp", "All-star game"), "Camp" for one flagged without a label, else
// null; `campNoun` spells it as a game ("camp game", "all-star game").
const campTagOf = (row) => textOf(row?.campLabel) || (row?.camp === true ? "Camp" : null);
const campNoun = (tag) => `${tag.toLowerCase().replace(/\s*\bgames?$/, "")} game`;

// "Nike EYBL 16U, Team Why Not, 17 games": whichever of the three are
// named; a camp says so ("Pangos All-American Camp, Team Kuminga (Pangos),
// 4 games · Camp"), and so does the play group the payload files a line
// under (D7: "Coastal League, Harbor Prep School, 4 games · School"), other
// events included ("· Other events", the group Game by Game lists them
// under), so no line on a season with tagged lines reads as unfiled. A
// camp's own label ("All-star game") wins over its group's.
const lineGroupTag = (c) => {
  const group = textOf(c?.group);
  return Object.prototype.hasOwnProperty.call(PLAY_GROUP_LABELS, group) ? PLAY_GROUP_LABELS[group] : null;
};

const competitionLine = (c) => {
  const games = num(c?.games);
  const line = [textOf(c?.competition), textOf(c?.team), games > 0 && countOf(games, "game")].filter(Boolean).join(", ");
  const tag = campTagOf(c) || lineGroupTag(c);
  return line && tag ? `${line} · ${tag}` : line;
};

// "4 camp games are not counted in the season's numbers.", naming the kinds
// the season's rows show ("camp and all-star games"); "" without camp games.
const campNoteOf = (campGames, tags) => {
  if (!(campGames > 0)) return "";
  const kinds = [...new Set(tags.map((tag) => campNoun(tag).replace(/ game$/, "")))];
  const noun = `${kinds.length ? kinds.join(" and ") : "camp"} game`;
  return `${countOf(campGames, noun)} ${campGames === 1 ? "is" : "are"} not counted in the season's numbers.`;
};

// The chart legend's entry for its camp marks: "Camp", "All-star game", or
// "Camp or all-star game" when the season has both.
const campLegendOf = (tags) => (tags.length ? tags.map((tag, i) => (i ? tag.toLowerCase() : tag)).join(" or ") : null);

// "W 72-65 vs. Team United": the result, the score from the athlete's side
// and the opponent, whichever are known; `words` spells the result out for
// screen readers ("win 72-65 vs. Team United").
const matchupOf = (p, words = false) => {
  const result = RESULT_WORDS[p?.result] ? (words ? RESULT_WORDS[p.result] : p.result) : "";
  const us = num(p?.teamScore);
  const them = num(p?.opponentScore);
  const opponent = textOf(p?.opponent);
  return [result, us != null && them != null && `${us}-${them}`, opponent && `vs. ${opponent}`].filter(Boolean).join(" ");
};

// The place closes a screen-reader line: "Nike EYBL - Peach Jam in Augusta, SC".
const withPlace = (parts, place) => {
  const out = parts.filter(Boolean);
  if (!place) return out;
  return out.length ? [...out.slice(0, -1), `${out[out.length - 1]} in ${place}`] : [`in ${place}`];
};

// One point of the season chart, with what its popover says (Kobi's 10/2
// note: the opponent and the place, "v. Team United > Augusta, SC"). A
// game: its date and event (`title`, "Jul 17 · Nike EYBL - Peach Jam"), the
// `matchup` ("W 72-65 vs. Team United") and the place (`location`,
// "Augusta, SC"). An event: its name, then the team and its games (`sub`,
// "Team Why Not · 4 games") and the place. `campLabel` tags a camp or
// all-star game, and `detail` says the point for screen readers ("win 72-65
// vs. Team United, Nike EYBL - Peach Jam in Augusta, SC"). The payload's
// keys pass through. Every key past the label and the points may be missing
// (older payloads, SportsRadar and partner seasons): the popover then shows
// what there is.
function seasonPoint(p, kind) {
  const label = textOf(p.label) || textOf(p.date);
  const event = textOf(p.event);
  const place = textOf(p.location);
  const team = textOf(p.team);
  const games = num(p.games);
  const tag = campTagOf(p);
  const point = {
    label,
    me: num(p.ppg),
    date: textOf(p.date) || null,
    event: event || null,
    location: place || null,
    team: team || null,
    games,
    camp: !!tag,
    campLabel: tag,
  };
  if (kind === "games") {
    return {
      ...point,
      opponent: textOf(p.opponent) || null,
      result: RESULT_WORDS[p.result] ? p.result : null,
      teamScore: num(p.teamScore),
      opponentScore: num(p.opponentScore),
      title: [label, event].filter(Boolean).join(" · "),
      matchup: matchupOf(p) || null,
      sub: null,
      detail: withPlace([tag && campNoun(tag), matchupOf(p, true), event], place).join(", "),
    };
  }
  const gamesText = games > 0 ? countOf(games, tag ? campNoun(tag) : "game") : tag ? campNoun(tag) : "";
  // An event's label ends its name ("EYBL - Peach Jam" for "Nike EYBL -
  // Peach Jam"); the name is said again only when the label cuts it short.
  const named = event && !(label && event.toLowerCase().endsWith(label.toLowerCase()));
  return {
    ...point,
    competition: textOf(p.competition) || null,
    title: event || label,
    matchup: null,
    sub: [team, games > 0 && countOf(games, "game")].filter(Boolean).join(" · ") || null,
    detail: withPlace([named && event, team, gamesText], place).join(", "),
  };
}

function buildCurrentSeason(ci, firstName, rankScope = {}) {
  const seasons = (Array.isArray(ci.seasons) ? ci.seasons : [])
    .filter((s) => s && textOf(s.label))
    .map((s) => {
      const stats = s.stats || {};
      const games = num(s.games);
      const series = (Array.isArray(s.series) ? s.series : []).filter((p) => p && typeof p === "object");
      const kind = SEASON_KINDS.includes(s.seriesKind) ? s.seriesKind : series.length ? "events" : "none";
      const lines = Array.isArray(s.competitions) ? s.competitions.filter((c) => c && typeof c === "object") : [];
      // Per game (that game's points) or per event (its average); none
      // for a season of totals.
      const points = kind === "none" ? [] : series.map((p) => seasonPoint(p, kind));
      const pointTags = [...new Set(points.map((p) => p.campLabel).filter(Boolean))];
      const campGames = num(s.campGames) > 0 ? num(s.campGames) : 0;
      return {
        label: textOf(s.label),
        games,
        ppg: num(stats.ppg),
        competitions: lines.map(competitionLine).filter(Boolean),
        numbers: [
          { label: "GP", value: games == null ? null : `${games}` },
          { label: "PPG", value: fmt1(num(stats.ppg)) },
          { label: "RPG", value: fmt1(num(stats.rpg)) },
          { label: "APG", value: fmt1(num(stats.apg)) },
        ],
        kind,
        points,
        // The games in camps, outside `games` and the numbers, and the note
        // saying so under them.
        campGames,
        campNote: campNoteOf(campGames, [...new Set([...lines.map(campTagOf), ...pointTags].filter(Boolean))]),
        campLegend: campLegendOf(pointTags),
      };
    });
  if (!seasons.length) return null;
  const labels = seasons.map((s) => s.label);
  const defaultSeason = labels.includes(ci.defaultSeason) ? ci.defaultSeason : labels[0];
  const seasonOf = (label) => (labels.includes(label) ? label : defaultSeason);
  return {
    seasons,
    defaultSeason,
    comparison: peerComparison(ci.peers, seasonOf, rankScope),
    teammates: teamComparison(ci.teammates, seasonOf),
    firstName,
  };
}

// The athlete against the peer group (the payload's `peers`): the group's
// average for the bars, its name for the caption, and the comparison rows.
// The athlete's numbers are their own in the group's season, like for
// like. The PPG row reads from the sign; the percentile row carries the
// same-state rank (the blue "State" chip) or the group's size. A group the
// athlete is not in now (`rankScope`, peerRankScope: stand-in stats, a group
// of another level) names itself on the percentile row ("PPG in Nevada 5A
// 2026"), and one the card cannot name shows no percentile row. null
// without a group, or with nothing in it to compare.
function peerComparison(peers, seasonOf, rankScope = {}) {
  if (!peers || typeof peers !== "object") return null;
  const avg = peers.averages || {};
  const mine = peers.athlete || {};
  const avgName = textOf(peers.comparisonLabel) || "League avg";
  const avgLower = avgName.toLowerCase();
  const size = num(peers.size);
  const players = size > 0 ? countOf(size, "player") : "";
  const ppg = num(mine.PPG);
  const avgPpg = num(avg.PPG);
  const apg = num(mine.APG);
  const avgApg = num(avg.APG);
  const top = num(peers.percentiles?.PPG);
  const stateTop = num(peers.state?.percentiles?.PPG);
  const stateCode = textOf(peers.state?.code);
  const position = textOf(peers.position);

  const tiles = [];
  if (ppg != null && avgPpg != null) {
    const diff = Math.round((ppg - avgPpg) * 10) / 10;
    const side = diff > 0 ? "above" : diff < 0 ? "below" : "at";
    tiles.push({
      value: `${diff > 0 ? "+" : ""}${fmt1(diff)}`,
      label: `PPG ${side} ${avgLower}`,
      chip: { label: `${diff > 0 ? "↑ " : diff < 0 ? "↓ " : ""}${fmt1(ppg)} vs. ${fmt1(avgPpg)} PPG`, variant: diff > 0 ? "green" : "gray" },
    });
  }
  if (top != null && !rankScope.hidden) {
    tiles.push({
      value: `Top ${top}%`,
      label: rankScope.foreign ? `PPG in ${rankScope.group}` : position ? `PPG among ${position.toLowerCase()}` : "PPG in the league",
      chip:
        stateTop != null && stateCode
          ? { label: `Top ${stateTop}% in ${stateCode}`, variant: "blue" }
          : players
            ? { label: players, variant: "gray" }
            : null,
    });
  }
  if (apg != null && avgApg > 0) {
    tiles.push({
      value: fmt1(apg),
      label: `APG, ${fmt1(apg / avgApg)}× ${avgLower}`,
      chip: { label: `Avg: ${fmt1(avgApg)} APG`, variant: "green" },
    });
  }
  if (avgPpg == null && !tiles.length) return null;

  const label = textOf(peers.label);
  const caption = label && players ? `Compared with ${label} (${players})` : label || players ? `Compared with ${label || players}` : "";
  return { season: seasonOf(peers.season), label, avgName, size, ppg, avgPpg, apg, avgApg, caption, tiles };
}

// The athlete against their team (the payload's `teammates`): PPG, APG and
// RPG with the team's average and the athlete's rank on it ("#2 of 12 in
// PPG"). The payload carries no other player's line. A roster whose own
// season is not on file (a partner team's "2026-27") shows with the season
// shown first, so its caption names its season (`rosterSeason`) rather than
// read as that season's team. null without team data.
function teamComparison(team, seasonOf) {
  if (!team || typeof team !== "object") return null;
  const rows = ["PPG", "APG", "RPG"]
    .map((stat) => {
      const you = num(team.athlete?.[stat]);
      const avg = num(team.averages?.[stat]);
      const rank = num(team.rank?.[stat]?.rank);
      const of = num(team.rank?.[stat]?.of);
      if (you == null || avg == null) return null;
      return { stat, you, avg, rank: rank && of ? `#${rank} of ${of} in ${stat}` : null, youText: fmt1(you), avgText: fmt1(avg) };
    })
    .filter(Boolean);
  if (!rows.length) return null;
  const size = num(team.size);
  const own = team.season == null ? "" : String(team.season).trim();
  const season = seasonOf(own);
  const rosterSeason = own && own !== season ? (/\bseason\b/i.test(own) ? own : `${own} season`) : null;
  return {
    season,
    rosterSeason,
    caption: [textOf(team.team), textOf(team.competition), rosterSeason, size > 0 && countOf(size, "player")].filter(Boolean).join(", "),
    rows,
  };
}

// ---------- the peer group's line ----------
// The athlete's line in a live peer group's competition season (the
// payload's `peers`, source "competition", or analytics.peerSource when the
// group names none): `line` by stat label, its `name` ("Nike EYBL 16U
// 2026") and the athlete's `games` in it (the group's own count, the games
// the line divides by; else the seasons on file). null for the cohort
// snapshot, no group, or a line with no numbers.
function groupLine(peers, peerSource, seasons) {
  if (!peers || typeof peers !== "object") return null;
  if ((peers.source || peerSource) !== "competition") return null;
  const line = peers.athlete && typeof peers.athlete === "object" ? peers.athlete : null;
  if (!line || !Object.values(line).some((v) => num(v) != null)) return null;
  const games = num(peers.games) > 0 ? num(peers.games) : gamesIn(seasons, peers);
  return { line, name: competitionSeasonOf(peers) || null, games };
}

// The group's competition season, as the backend names the group without
// its position scope: "Nike EYBL 16U" in "2026" reads "Nike EYBL 16U 2026";
// a season name the competition already carries is not repeated.
function competitionSeasonOf(peers) {
  const competition = textOf(peers.competition);
  const season = textOf(peers.seasonName);
  if (!competition) return "";
  return season && !competition.toLowerCase().includes(season.toLowerCase()) ? `${competition} ${season}` : competition;
}

// The athlete's games in the group's competition season: that
// competition's lines in the season the group covers, added up (a player
// listed for two teams in it counts both, as the group's line does). null
// when the seasons on file do not show it.
function gamesIn(seasons, peers) {
  const competition = textOf(peers.competition);
  const label = textOf(peers.season);
  const season = (Array.isArray(seasons) ? seasons : []).find((s) => label && textOf(s?.label) === label);
  if (!competition || !season) return null;
  const games = (Array.isArray(season.competitions) ? season.competitions : [])
    .filter((c) => textOf(c?.competition) === competition)
    .reduce((sum, c) => sum + (num(c.games) || 0), 0);
  return games > 0 ? games : null;
}

// ---------- where athletes valued in the range compete ----------
// The pills under "Athletes valued in your range typically compete at:" (the
// CacheValue in context card), as data by sport family, gender and level:
// five bands each, from the top 5% down (competeBand), each pill
// { bold, rest } with its bold text unique in its band (it keys the pill).
//  - Basketball by gender (Kobi, 2026-10-07: a women's college profile read
//    "NBA Draft, G League Elite"): men's tiers name the NBA, the G League and
//    the men's leagues abroad; women's the WNBA, EuroLeague Women, the
//    women's leagues abroad and the offseason pro leagues. With no gender
//    known they name no pro league of either.
//  - High school tiers are college programs that men's and women's teams
//    share; a girl's, and an unknown gender's, name no boys' prep academy.
//  - Football is the men's game in every feed CACHE reads (team_kinds:
//    SportsRadar football is men's), so an unknown gender reads the men's
//    tiers. College and pro football programs are men's teams, so a woman's
//    football tiers are the neutral ones.
//  - Any other sport, or none, reads neutral tiers that name no league.
const BASKETBALL_MEN = {
  HS: [
    [{ bold: "High-major D1", rest: "ACC, Big Ten, SEC, Big 12" }, { bold: "Top prep", rest: "IMG, Montverde, Brewster" }],
    [{ bold: "Mid-major D1", rest: "Mountain West, CAA, Horizon" }, { bold: "Power 6 HBCU", rest: "SWAC, MEAC" }, { bold: "Top G5", rest: "AAC, Sun Belt" }],
    [{ bold: "Low-major D1", rest: "SWAC, NEC, MEAC" }, { bold: "Prep / postgrad", rest: "IMG, Brewster, Sunrise" }, { bold: "High-major JUCO", rest: "NJCAA Div I" }],
    [{ bold: "D2", rest: "GLIAC, PSAC, SSC" }, { bold: "NAIA", rest: "independent colleges" }, { bold: "JUCO", rest: "NJCAA Div I-II" }],
    [{ bold: "D3", rest: "NESCAC, UAA" }, { bold: "NAIA", rest: "development route" }, { bold: "JUCO", rest: "NJCAA Div II-III" }],
  ],
  College: [
    [{ bold: "Pro pathway", rest: "NBA Draft, G League Elite" }, { bold: "High-major transfer", rest: "Power conferences" }],
    [{ bold: "High-major transfer", rest: "ACC, Big Ten, SEC" }, { bold: "Top G5", rest: "AAC, Mountain West" }],
    [{ bold: "Mid-major D1", rest: "CAA, Horizon, Southland" }, { bold: "Top G5", rest: "AAC, Sun Belt" }],
    [{ bold: "Low-major D1", rest: "SWAC, NEC, MEAC" }, { bold: "D2", rest: "GLIAC, PSAC" }],
    [{ bold: "D2 / D3", rest: "regional programs" }, { bold: "NAIA", rest: "independent colleges" }],
  ],
  Pro: [
    [{ bold: "NBA / Top Euro", rest: "NBA, EuroLeague" }, { bold: "National teams", rest: "FIBA circuit" }],
    [{ bold: "G League", rest: "NBA affiliates" }, { bold: "Top international", rest: "ACB, NBL, LNB" }],
    [{ bold: "International pro", rest: "Europe, Asia, LatAm" }, { bold: "G League camps", rest: "Exhibit 10" }],
    [{ bold: "International pro", rest: "second divisions" }, { bold: "Pro combines", rest: "showcase circuit" }],
    [{ bold: "Semi-pro", rest: "regional leagues" }, { bold: "Showcases", rest: "open runs, combines" }],
  ],
};

// The men's high school tiers with the boys' prep academies left unnamed:
// a girl's, and one whose gender nobody gave.
const BASKETBALL_HS_ANY_GENDER = [
  [{ bold: "High-major D1", rest: "ACC, Big Ten, SEC, Big 12" }, { bold: "Top prep", rest: "national prep programs" }],
  [{ bold: "Mid-major D1", rest: "Mountain West, CAA, Horizon" }, { bold: "Power 6 HBCU", rest: "SWAC, MEAC" }, { bold: "Top G5", rest: "AAC, Sun Belt" }],
  [{ bold: "Low-major D1", rest: "SWAC, NEC, MEAC" }, { bold: "Prep / postgrad", rest: "academy programs" }, { bold: "High-major JUCO", rest: "NJCAA Div I" }],
  [{ bold: "D2", rest: "GLIAC, PSAC, SSC" }, { bold: "NAIA", rest: "independent colleges" }, { bold: "JUCO", rest: "NJCAA Div I-II" }],
  [{ bold: "D3", rest: "NESCAC, UAA" }, { bold: "NAIA", rest: "development route" }, { bold: "JUCO", rest: "NJCAA Div II-III" }],
];

const BASKETBALL_WOMEN = {
  HS: BASKETBALL_HS_ANY_GENDER,
  College: [
    [{ bold: "Pro pathway", rest: "WNBA Draft, overseas pro" }, { bold: "High-major transfer", rest: "Power conferences" }],
    [{ bold: "High-major transfer", rest: "SEC, Big Ten, ACC, Big East" }, { bold: "Top mid-major", rest: "WCC, A-10, Ivy" }],
    [{ bold: "Mid-major D1", rest: "MVC, Summit, Horizon" }, { bold: "Upper mid-major", rest: "AAC, Mountain West, Sun Belt" }],
    [{ bold: "Low-major D1", rest: "SWAC, NEC, MEAC" }, { bold: "D2", rest: "GLIAC, PSAC" }],
    [{ bold: "D2 / D3", rest: "regional programs" }, { bold: "NAIA", rest: "independent colleges" }],
  ],
  Pro: [
    [{ bold: "WNBA / Top Euro", rest: "WNBA, EuroLeague Women" }, { bold: "National teams", rest: "FIBA circuit" }],
    [{ bold: "Top international", rest: "LFB, Liga Femenina, KBSL" }, { bold: "Offseason pro", rest: "Unrivaled, Athletes Unlimited" }],
    [{ bold: "International pro", rest: "WNBL, Europe, Asia" }, { bold: "WNBA camps", rest: "training camp contracts" }],
    [{ bold: "International pro", rest: "second divisions" }, { bold: "Pro combines", rest: "showcase circuit" }],
    [{ bold: "Semi-pro", rest: "regional leagues" }, { bold: "Showcases", rest: "open runs, combines" }],
  ],
};

// No gender on file: the college programs both share, no pro league named.
const BASKETBALL_ANY_GENDER = {
  HS: BASKETBALL_HS_ANY_GENDER,
  College: [
    [{ bold: "Pro pathway", rest: "pro draft, overseas pro" }, { bold: "High-major transfer", rest: "Power conferences" }],
    [{ bold: "High-major transfer", rest: "ACC, Big Ten, SEC" }, { bold: "Top mid-major", rest: "A-10, WCC, Mountain West" }],
    [{ bold: "Mid-major D1", rest: "CAA, Horizon, MVC" }, { bold: "Upper mid-major", rest: "AAC, Sun Belt" }],
    [{ bold: "Low-major D1", rest: "SWAC, NEC, MEAC" }, { bold: "D2", rest: "GLIAC, PSAC" }],
    [{ bold: "D2 / D3", rest: "regional programs" }, { bold: "NAIA", rest: "independent colleges" }],
  ],
  Pro: [
    [{ bold: "Top pro", rest: "top domestic and Euro leagues" }, { bold: "National teams", rest: "FIBA circuit" }],
    [{ bold: "Top international", rest: "top European leagues" }, { bold: "Pro development", rest: "training camps, affiliates" }],
    [{ bold: "International pro", rest: "Europe, Asia, Australia" }, { bold: "Pro camps", rest: "camp contracts" }],
    [{ bold: "International pro", rest: "second divisions" }, { bold: "Pro combines", rest: "showcase circuit" }],
    [{ bold: "Semi-pro", rest: "regional leagues" }, { bold: "Showcases", rest: "open runs, combines" }],
  ],
};

const FOOTBALL_MEN = {
  HS: [
    [{ bold: "Power 4", rest: "SEC, Big Ten, Big 12, ACC" }, { bold: "Top prep", rest: "IMG, Bishop Gorman" }],
    [{ bold: "Group of 5", rest: "AAC, MAC, Sun Belt" }, { bold: "FCS top tier", rest: "Big Sky, CAA" }],
    [{ bold: "FCS", rest: "Big Sky, MVFC" }, { bold: "D2", rest: "GLIAC, MIAA" }, { bold: "Prep / postgrad", rest: "IMG, academies" }],
    [{ bold: "D2 / D3", rest: "regional programs" }, { bold: "NAIA", rest: "independent colleges" }, { bold: "JUCO", rest: "NJCAA" }],
    [{ bold: "D3 / NAIA", rest: "development route" }, { bold: "JUCO", rest: "NJCAA Div I-III" }],
  ],
  College: [
    [{ bold: "NFL Draft pathway", rest: "Senior Bowl, NFL Combine" }, { bold: "Power 4 starter", rest: "playoff contenders" }],
    [{ bold: "Power 4 transfer", rest: "SEC, Big Ten, Big 12, ACC" }, { bold: "Top Group of 5", rest: "AAC, Mountain West" }],
    [{ bold: "Group of 5", rest: "MAC, Sun Belt, C-USA" }, { bold: "FCS top tier", rest: "Big Sky, MVFC" }],
    [{ bold: "FCS", rest: "CAA, Southland, SWAC" }, { bold: "D2", rest: "GLIAC, MIAA" }],
    [{ bold: "D2 / D3", rest: "regional programs" }, { bold: "NAIA", rest: "independent colleges" }],
  ],
  Pro: [
    [{ bold: "NFL", rest: "active rosters" }, { bold: "NFL starters", rest: "core rotation" }],
    [{ bold: "NFL depth", rest: "practice squads" }, { bold: "CFL", rest: "starting roles" }],
    [{ bold: "CFL / UFL", rest: "pro rosters" }, { bold: "NFL camps", rest: "tryouts, rookie minicamps" }],
    [{ bold: "Arena / IFL", rest: "indoor pro" }, { bold: "Pro combines", rest: "regional combines" }],
    [{ bold: "Semi-pro", rest: "regional leagues" }, { bold: "Showcases", rest: "open tryouts, combines" }],
  ],
};

// Any sport without tables of its own, or none: levels, no league names.
const NEUTRAL_TIERS = {
  HS: [
    [{ bold: "Top national level", rest: "elite college programs" }, { bold: "National showcases", rest: "invite-only events" }],
    [{ bold: "High college level", rest: "strong national programs" }, { bold: "Regional showcases", rest: "top events" }],
    [{ bold: "College level", rest: "established programs" }, { bold: "Junior college", rest: "two-year programs" }],
    [{ bold: "Small college", rest: "regional programs" }, { bold: "Junior college", rest: "development route" }],
    [{ bold: "Regional programs", rest: "small colleges" }, { bold: "Club play", rest: "development route" }],
  ],
  College: [
    [{ bold: "Pro pathway", rest: "pro leagues, overseas pro" }, { bold: "Top national level", rest: "leading programs" }],
    [{ bold: "Top national level", rest: "leading programs" }, { bold: "Upper-level transfer", rest: "strong conferences" }],
    [{ bold: "Upper college level", rest: "strong conferences" }, { bold: "Mid-level programs", rest: "regional conferences" }],
    [{ bold: "Mid-level programs", rest: "regional conferences" }, { bold: "Small college", rest: "regional programs" }],
    [{ bold: "Small college", rest: "regional programs" }, { bold: "Club play", rest: "development route" }],
  ],
  Pro: [
    [{ bold: "Top pro level", rest: "leading leagues" }, { bold: "National teams", rest: "international play" }],
    [{ bold: "Established pro", rest: "top domestic and foreign leagues" }, { bold: "Development pro", rest: "affiliate programs" }],
    [{ bold: "International pro", rest: "overseas leagues" }, { bold: "Pro camps", rest: "tryouts, camp contracts" }],
    [{ bold: "Lower-division pro", rest: "second divisions" }, { bold: "Pro combines", rest: "showcase circuit" }],
    [{ bold: "Semi-pro", rest: "regional leagues" }, { bold: "Showcases", rest: "open tryouts, combines" }],
  ],
};

// sport family -> gender ("men", "women", "any" for none known) -> level.
export const COMPETE_TIERS = {
  basketball: { men: BASKETBALL_MEN, women: BASKETBALL_WOMEN, any: BASKETBALL_ANY_GENDER },
  football: { men: FOOTBALL_MEN, women: NEUTRAL_TIERS, any: FOOTBALL_MEN },
  other: { any: NEUTRAL_TIERS },
};

const GENDER_KEYS = { 1: "men", 2: "women" };

const sportFamilyOf = (sport) => {
  const name = String(sport || "");
  if (/basketball/i.test(name)) return "basketball";
  if (/football/i.test(name)) return "football";
  return "other";
};

// The band a percentile ("Top N%") falls in: the top 5%, 15%, 35%, 60%, then
// everyone else.
const competeBand = (pct) => (pct <= 5 ? 0 : pct <= 15 ? 1 : pct <= 35 ? 2 : pct <= 60 ? 3 : 4);

// The pills for a sport, a gender (1 men, 2 women, 0 unknown), a level
// ("HS", "College", "Pro"; none reads the high school and grassroots tiers)
// and a percentile, as copies so no reader can change the tables.
export function competeTiers(sport, gender, level, pct) {
  const family = COMPETE_TIERS[sportFamilyOf(sport)];
  const tiers = family[GENDER_KEYS[gender]] || family.any;
  const rows = tiers[level] || tiers.HS;
  return rows[competeBand(pct)].map((pill) => ({ ...pill }));
}

// The first word of a stored gender or a team's gender label
// (team_kinds._GENDER_WORDS): 1 men, 2 women.
const GENDER_WORDS = {
  m: 1, male: 1, man: 1, men: 1, mens: 1, boy: 1, boys: 1,
  f: 2, female: 2, w: 2, woman: 2, women: 2, womens: 2, girl: 2, girls: 2,
};

// 1 men, 2 women, 0 unknown for a stored gender (1 or 2, "M" or "F") or a
// label ("Men/Boys", "Women/Girls", "Women's Basketball"), as the backend's
// team_kinds._gender_code reads one. 0 and blank are unknown, never men.
const genderCodeOf = (value) => {
  if (typeof value === "number") return value === 1 || value === 2 ? value : 0;
  if (typeof value !== "string") return 0;
  const text = value.trim().toLowerCase().replace(/[^a-z12/ ]/g, "");
  if (text === "1" || text === "2") return Number(text);
  return GENDER_WORDS[text.split(/[/ ]+/).find(Boolean)] || 0;
};

// The gender a league key names on its own (team_kinds._suffix_gender and
// the onboarding's teamSportKey): "wbb", "ncaawb" and "..._girls_..." are
// women's; "mbb", "ncaamb", "..._boys_..." and football's "fb" men's. A key
// that says neither (a Cerebro circuit's) is 0.
const leagueGenderOf = (league) => {
  const key = typeof league === "string" ? league.trim().toLowerCase() : "";
  if (!key) return 0;
  if (/(?:^|_)(?:girls|women|womens)(?:_|$)/.test(key) || /wbb?$/.test(key)) return 2;
  if (/(?:^|_)(?:boys|men|mens)(?:_|$)/.test(key) || /(?:mbb?|fb)$/.test(key)) return 1;
  return 0;
};

// The gender the tiers are for: the athlete's own, else what their sport's
// name says, else their teams' the way the backend reads it when the
// athlete gave none (identity_match.gender_code): the team list's label (the
// backend's read of each team's league: a partner team's gender, a Cerebro
// league's), else a league key's own, the main team first. The teams of
// one profile share a division (adding a team of another is refused), so
// any one that says is enough. 0 when nothing says.
export function competeGender(athlete = {}, identity = {}, sport = "") {
  const own = genderCodeOf(athlete?.gender) || genderCodeOf(sport);
  if (own) return own;
  const objects = (list) => list.filter((t) => t && typeof t === "object");
  const mainFirst = (list, isMain) => [...list.filter(isMain), ...list.filter((t) => !isMain(t))];
  const teams = objects(Array.isArray(athlete?.allTeams) ? athlete.allTeams : []);
  const current = identity?.current && typeof identity.current === "object" ? objects(Object.values(identity.current)) : [];
  const reads = [
    ...mainFirst(teams, (t) => !!t.isPrimary).map((t) => genderCodeOf(t.gender) || leagueGenderOf(t.league)),
    ...mainFirst(current, (t) => !!t.primary).map((t) => leagueGenderOf(t.league)),
    leagueGenderOf(athlete?.league),
  ];
  return reads.find(Boolean) || 0;
}

// The sports whose men's and women's games are separate competitions, each
// with its own leagues and levels, by the codes table's name in any case:
// the backend's list (users/sport_gender.py GENDERED_SPORTS). Football is
// the men's game in this app, and baseball and softball name their game
// themselves.
const GENDERED_SPORTS = new Set([
  "basketball", "soccer", "volleyball", "beach volleyball", "hockey", "ice hockey", "field hockey",
  "lacrosse", "water polo", "rugby", "tennis", "golf", "swimming", "swimming & diving", "diving",
  "track & field", "track & field/cross", "cross country", "wrestling", "gymnastics", "rowing",
  "fencing", "bowling", "skiing",
]);

// The sport as the profile names it: "Women's basketball" for a woman (2,
// or a stored "F") in a gendered sport, the way the backend names their
// group (sport_gender.sport_with_gender); the codes table's name otherwise.
// A man keeps "Basketball", as before, and so does a profile whose gender
// nobody gave.
export const sportLabelFor = (sport, gender) => {
  const name = String(sport || "").replace(/\s+/g, " ").trim();
  if (genderCodeOf(gender) !== 2 || !GENDERED_SPORTS.has(name.toLowerCase())) return sport || "";
  return `Women's ${name.toLowerCase()}`;
};

// How the stats refresh found a stats doc that is not the athlete's main
// record (statistics.cerebro.via; identity_match.FALLBACK_VIAS and
// cerebro_records.BORROWED_VIAS): a linked record, an identity kept from an
// earlier team, a strict name match, a roster spot on another team.
const STAND_IN_VIAS = ["linked_record", "prior_identity", "strict_match", "team_roster"];

// A roster spot the athlete holds on one of their teams
// (cerebro_records.VIA_TEAM_ROSTER, teams[].playerId): the stats of a team
// they play for now, never a stand-in. Only a spot no team on the profile
// holds any more (the team was removed since the refresh, and an outage
// keeps the stored stats until the next good one) stands in, and so does
// any spot of a profile that lists its teams and holds none. A payload
// without the team lists says nothing against the via, which named a spot
// on a current team.
const VIA_TEAM_ROSTER = "team_roster";
const idText = (v) => (typeof v === "string" || typeof v === "number" ? String(v).trim().toLowerCase() : "");

const onCurrentTeamSpot = (stats, athlete) => {
  const lists = [athlete?.teams, athlete?.allTeams].filter(Array.isArray);
  if (!lists.length) return true;
  const teams = lists.flat().filter((t) => t && typeof t === "object");
  const record = idText(stats.cerebro_id) || idText(stats.cerebro_identity?.id);
  return !!record && teams.some((t) => idText(t.playerId) === record);
};

// The stats stand in for the athlete's own team feed: the about payload's
// statsStandIn (the backend's placeholder, the record of a team they have
// left), the stats doc's own placeholder, or a doc found through another
// record (STAND_IN_VIAS) that is not a current team's roster spot. Its peer
// group ranks that record's competition.
export const statsAreStandIn = (ci, athlete) => {
  const doc = athlete?.statistics?.cerebro;
  const stats = doc && typeof doc === "object" ? doc : {};
  if ((ci?.statsStandIn && typeof ci.statsStandIn === "object") || stats.placeholder) return true;
  if (!STAND_IN_VIAS.includes(stats.via)) return false;
  return !(stats.via === VIA_TEAM_ROSTER && onCurrentTeamSpot(stats, athlete));
};

const LEVEL_KEYS = { hs: "HS", college: "College", pro: "Pro" };

// The percentile that picks the tier band: the peer group's PPG rank ("Top
// 14%") when it ranks the athlete at the level the tiers are for, else the
// CacheScore band (70 and up reads as the top 15%, 50 and up the top 30%,
// anything else the top half). Stats that stand in for the athlete's feed
// rank another team's competition, and a peer group the backend puts at
// another level (analytics.peerLevel: a college freshman's high school
// group) ranks another level, so neither picks the band: a high school rank
// never makes a college athlete's tier "Pro pathway". A payload without
// peerLevel says nothing about the level, so only the stand-in decides.
export function competePercentile({ national, peerLevel, level, standIn, cacheScore } = {}) {
  const groupLevel = LEVEL_KEYS[String(peerLevel || "").trim().toLowerCase()] || "";
  const ranksLevel = !standIn && !(groupLevel && level && groupLevel !== level);
  const peer = ranksLevel ? num(national?.PPG) : null;
  if (peer != null) return peer;
  const score = num(cacheScore);
  return score != null ? (score >= 70 ? 15 : score >= 50 ? 30 : 50) : 50;
}

// ---------- whose ranks the box score shows ----------
// A level's name for a group of it, the radar tab of a peer group of
// another level than the athlete's.
const LEVEL_GROUP_NAMES = { HS: "High school", College: "College", Pro: "Pro" };

// A group's name as a rank carries it: the backend's label without the
// default scope ("Nike EYBL 16U 2026, all positions" reads "Nike EYBL 16U
// 2026"), a scope that narrows the group kept ("Nike EYBL 17U 2026,
// Guards"). The box score subtitle and the radar caption keep the whole
// label. "" for no label, or one that is the default scope alone.
const rankGroupName = (label) => textOf(label).replace(/(?:^|,\s*)all positions$/i, "").trim();

// A rank that names its group, "Top 3% in Nevada 5A 2026", with the rank
// and the group apart for a card that sets the group in lighter type.
const namedRank = (top, scope, variant) => ({ label: `Top ${top}% in ${scope}`, rank: `Top ${top}%`, scope, variant });

// Whose ranks the box score shows (Kobi, 2026-10-07: a college freshman
// whose stats stand in from their high school had high school ranks that
// read as national ones). The peer group is the athlete's own unless their
// stats stand in for their team's feed (statsAreStandIn: the ranks are of
// the competition the record is from) or the backend puts the group at
// another level than theirs (peerLevel 'HS' for a college athlete). Such a
// group is `foreign`: its ranks name it, `group` (its label,
// rankGroupName) and `state` (the same-state subset's postal code: no
// state rank without one), and `levelName` names a group of another level
// ("High school"). `hidden` when it has no name: its ranks stay off the
// card rather than read as national ones. A payload without peerLevel says
// nothing about the level, so the stand-in alone decides, as for the
// competition tiers (competePercentile).
export function peerRankScope({ standIn = false, peerLevel = "", level = "", peerLabel = "", stateCode = "" } = {}) {
  const groupLevel = LEVEL_KEYS[String(peerLevel || "").trim().toLowerCase()] || "";
  const otherLevel = !!(groupLevel && level && groupLevel !== level);
  if (!standIn && !otherLevel) return { foreign: false, hidden: false, group: "", state: "", levelName: "" };
  const group = rankGroupName(peerLabel);
  return { foreign: true, hidden: !group, group, state: textOf(stateCode), levelName: otherLevel ? LEVEL_GROUP_NAMES[groupLevel] : "" };
}

// Derive brand-category fit from the athlete's REAL audience signals. This is
// not a backend "brand" table (none exists); it's a deterministic read of
// reach (follower tier) + engagement + verification, exactly what the design
// means by "based on your audience profile". Returns null with no social data.
function deriveBrand(totalFollowers, engagement, platforms, sport) {
  const followers = totalFollowers || 0;
  const eng = engagement || 0;
  const linkedCount = (platforms || []).filter((p) => p.linked).length;
  // With zero audience signals the card still renders (per the design) — the
  // reach score bottoms out and every category reads as the floor fit.

  // Follower tier -> brand tier language.
  const tier =
    followers >= 1000000 ? { name: "mega", brands: "Nike / Adidas tier" }
    : followers >= 500000 ? { name: "macro", brands: "Nike / Adidas tier" }
    : followers >= 50000 ? { name: "mid", brands: "Under Armour / New Balance tier" }
    : followers >= 10000 ? { name: "micro", brands: "Regional / emerging tier" }
    : { name: "nano", brands: "Local / emerging tier" };

  // Reach score 0..3 from tier, engagement bonus, multi-platform bonus.
  const tierScore = { nano: 0, micro: 1, mid: 2, macro: 3, mega: 3 }[tier.name];
  const engBonus = eng >= 5 ? 1 : eng >= 2.5 ? 0.5 : 0;
  const platBonus = linkedCount >= 3 ? 0.5 : 0;
  const reach = tierScore + engBonus + platBonus;

  const fitFor = (base) => {
    const s = reach + base;
    if (s >= 3) return { fit: "Strong fit", variant: "green" };
    if (s >= 1.5) return { fit: "Good fit", variant: "blue" };
    return { fit: "Moderate fit", variant: "yellow" };
  };

  // Exact design categories/monograms/tier copy; only the fit chip is derived
  // from the athlete's real reach + engagement.
  const cats = [
    { mono: "NK", name: "Athletic footwear", tier: "Nike / Adidas tier", base: 1 },
    { mono: "GT", name: "Sports nutrition", tier: "Regional / emerging tier", base: 0.5 },
    { mono: "UA", name: "Performance apparel", tier: "Under Armour / New Balance tier", base: 0.5 },
    { mono: "FS", name: "Financial services", tier: "College-focused brands", base: 0 },
    { mono: "CT", name: "Consumer tech", tier: "Regional / emerging tier", base: -0.5 },
    { mono: "BT", name: "Audio / lifestyle", tier: "Beats / Bose tier", base: -0.5 },
  ].map((c) => ({ mono: c.mono, name: c.name, tier: c.tier, ...fitFor(c.base) }));

  return { categories: cats, tierName: tier.name };
}
